import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';
import styles from './UserRegions.module.css';

const API = process.env.REACT_APP_API_BASE_URL;
const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const AVAILABLE_ROLES = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'regional_sales_head', label: 'Regional Sales Head' },
    { value: 'sales_rep', label: 'Sales Person' },
    { value: 'lab', label: 'Lab' },
];

const UserRegions = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Add User modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formUsername, setFormUsername] = useState('');
    const [formName, setFormName] = useState('');
    const [formPassword, setFormPassword] = useState('');
    const [formRole, setFormRole] = useState('');
    const [formCities, setFormCities] = useState([]);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // Reset Password state
    const [resetTarget, setResetTarget] = useState(null); // { userId, username }
    const [newPassword, setNewPassword] = useState('');
    const [resetting, setResetting] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    // Action menu state
    const [openMenuId, setOpenMenuId] = useState(null);
    const menuRef = useRef(null);

    // Delete / Restore state
    const [showDeactivated, setShowDeactivated] = useState(false);
    const [userQuery, setUserQuery] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { userId, username }

    // Regions fetched from /api/mappings/regions
    const [regions, setRegions] = useState([]);
    // Full region→cities mapping from /api/mappings
    const [regionCitiesMap, setRegionCitiesMap] = useState({});
    // Which region accordions are expanded in the modal
    const [expandedRegions, setExpandedRegions] = useState({});

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }
            const response = await axios.get(`${API}/api/users/users-with-regions`, authHeaders());
            setUsers(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch users and their regions.');
            console.error('Error fetching users with regions:', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Fetch regions list on mount
    useEffect(() => {
        const fetchRegions = async () => {
            try {
                const res = await axios.get(`${API}/api/mappings/regions`, authHeaders());
                setRegions(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Error fetching regions:', err);
            }
        };
        fetchRegions();
    }, []);

    // Fetch full region→cities mapping on mount
    useEffect(() => {
        const fetchMappings = async () => {
            try {
                const res = await axios.get(`${API}/api/mappings`, authHeaders());
                const mappings = res.data.regionMappings || [];
                const map = {};
                mappings.forEach(m => {
                    map[m.regionName] = (m.cities || []).map(c => c.cityName).sort();
                });
                setRegionCitiesMap(map);
            } catch (err) {
                console.error('Error fetching region-city mappings:', err);
            }
        };
        fetchMappings();
    }, []);

    const needsCities = formRole === 'sales_rep';

    const resetForm = () => {
        setFormUsername('');
        setFormName('');
        setFormPassword('');
        setFormRole('');
        setFormCities([]);
        setExpandedRegions({});
        setIsEditing(false);
        setEditingUser(null);
    };

    const handleEditClick = (user) => {
        setIsEditing(true);
        setEditingUser(user);
        setFormUsername(user.Username);
        setFormName(user.name || '');
        setFormRole(user.Role);
        setFormPassword('');

        // Pre-populate cities from user's assigned regions
        const userRegionNames = (user.RegionalCodes || []).map(rc => rc.regionName);
        const existingCities = [];
        const expanded = {};
        userRegionNames.forEach(rn => {
            const cities = regionCitiesMap[rn] || [];
            existingCities.push(...cities);
            expanded[rn] = true; // auto-expand assigned regions
        });
        setFormCities([...new Set(existingCities)]);
        setExpandedRegions(expanded);
        setShowAddModal(true);
    };

    const handleSaveUser = async () => {
        if (!formUsername.trim()) return toast.warn('Username is required.');
        if (!formName.trim()) return toast.warn('Name is required.');
        if (!formRole) return toast.warn('Please select a role.');
        if (needsCities && formCities.length === 0) {
            return toast.warn('Please select at least one city.');
        }

        setSaving(true);
        try {
            const payload = {
                username: formUsername.trim(),
                name: formName.trim(),
                role: formRole,
                cities: needsCities ? formCities : [],
            };
            if (isEditing) {
                const res = await axios.patch(`${API}/api/users/${editingUser.UserID}`, payload, authHeaders());
                toast.success(`User "${res.data.username}" updated successfully!`);
            } else {
                payload.password = formPassword;
                const res = await axios.post(`${API}/api/users/register`, payload, authHeaders());
                toast.success(`User "${res.data.username}" registered successfully!`);
            }
            resetForm();
            setShowAddModal(false);
            setLoading(true);
            fetchUsers();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || `Failed to ${isEditing ? 'update' : 'register'} user.`;
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    // Delete (deactivate) user
    const handleDeleteUser = async () => {
        if (!deleteConfirm) return;
        try {
            await axios.delete(`${API}/api/users/${deleteConfirm.userId}`, authHeaders());
            toast.success(`User "${deleteConfirm.username}" deactivated successfully`);
            setDeleteConfirm(null);
            setLoading(true);
            fetchUsers();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to deactivate user.';
            toast.error(msg);
        }
    };

    // Restore user
    const handleRestoreUser = async (userId, username) => {
        try {
            await axios.patch(`${API}/api/users/${userId}/restore`, {}, authHeaders());
            toast.success(`User "${username}" restored successfully`);
            setLoading(true);
            fetchUsers();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to restore user.';
            toast.error(msg);
        }
    };

    // Reset password
    const handleResetPassword = async () => {
        if (!newPassword.trim()) return toast.warn('New password is required.');
        setResetting(true);
        try {
            const res = await axios.patch(
                `${API}/api/users/${resetTarget.userId}/reset-password`,
                { newPassword },
                authHeaders()
            );
            toast.success(res.data.message || `Password reset successfully for "${resetTarget.username}"`);
            setResetTarget(null);
            setNewPassword('');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to reset password.';
            toast.error(msg);
        } finally {
            setResetting(false);
        }
    };

    // Close action menu on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCityToggle = (city) => {
        setFormCities(prev =>
            prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
        );
    };

    const toggleRegion = (regionName) => {
        setExpandedRegions(prev => ({ ...prev, [regionName]: !prev[regionName] }));
    };

    // Select/deselect all cities in a region
    const handleRegionSelectAll = (regionName) => {
        const cities = regionCitiesMap[regionName] || [];
        const allSelected = cities.every(c => formCities.includes(c));
        if (allSelected) {
            setFormCities(prev => prev.filter(c => !cities.includes(c)));
        } else {
            setFormCities(prev => [...new Set([...prev, ...cities])]);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    const userRole = localStorage.getItem('role');
    const canResetPassword = userRole === 'admin' || userRole === 'Super_Admin';

    // Text search across username / name / role / regions
    const uq = userQuery.trim().toLowerCase();
    const matchesQuery = (u) => {
        if (!uq) return true;
        const regions = (u.RegionalCodes || []).map(rc => rc.regionName).join(', ');
        return (
            (u.Username || '').toLowerCase().includes(uq) ||
            (u.name || '').toLowerCase().includes(uq) ||
            (u.Role || '').toLowerCase().includes(uq) ||
            regions.toLowerCase().includes(uq)
        );
    };

    // Separate active vs deactivated users
    const activeUsers = users.filter(u => !u.isDeactivated && matchesQuery(u));
    const deactivatedUsers = users.filter(u => u.isDeactivated && matchesQuery(u));

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Users and Their Regions</h2>
            </div>

            {/* ─── Toolbar: search (left) · actions (right) ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: '1 1 auto', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        placeholder="Search by username / name / role / region"
                        value={userQuery}
                        onChange={e => setUserQuery(e.target.value)}
                        style={{ width: '100%', maxWidth: 320, padding: '9px 12px', fontSize: 14, border: '1.5px solid #d0d7de', borderRadius: 8 }}
                    />
                    {deactivatedUsers.length > 0 && (
                        <label className={styles.toggleLabel}>
                            <input
                                type="checkbox"
                                checked={showDeactivated}
                                onChange={e => setShowDeactivated(e.target.checked)}
                            />
                            Show Deactivated ({deactivatedUsers.length})
                        </label>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className={styles.addUserBtn} onClick={() => setShowAddModal(true)}>+ Add New User</button>
                    <button
                        className={styles.addUserBtn}
                        title="Export to Excel"
                        aria-label="Export to Excel"
                        style={{ background: '#16a34a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px' }}
                        onClick={() => {
                            const rows = activeUsers.map(u => ({
                                Username: u.Username,
                                Name: u.name || '',
                                Role: u.Role,
                                Regions: (u.RegionalCodes || []).map(rc => rc.regionName).join(', '),
                            }));
                            if (!rows.length) return toast.warn('No users to export.');
                            const ws = XLSX.utils.json_to_sheet(rows);
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, 'Users');
                            XLSX.writeFile(wb, 'Users.xlsx');
                        }}
                    ><FaFileExcel size={20} /></button>
                </div>
            </div>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Regions</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {activeUsers.map(user => (
                        <tr key={user.UserID}>
                            <td>{user.Username}</td>
                            <td>{user.name || '-'}</td>
                            <td><span className={styles.roleBadge} data-role={user.Role}>{user.Role}</span></td>
                            <td>
                                {user.RegionalCodes && user.RegionalCodes.length > 0 ? (
                                    user.RegionalCodes.map(rc => rc.regionName).join(', ')
                                ) : (
                                    <span className={styles.noRegions}>No regions assigned</span>
                                )}
                            </td>
                            <td>
                                <div className={styles.actionMenu} ref={openMenuId === user.UserID ? menuRef : null}>
                                    <button
                                        className={styles.kebabBtn}
                                        onClick={() => setOpenMenuId(openMenuId === user.UserID ? null : user.UserID)}
                                        title="Actions"
                                    >
                                        &#8942;
                                    </button>
                                    {openMenuId === user.UserID && (
                                        <div className={styles.dropdownMenu}>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={() => { handleEditClick(user); setOpenMenuId(null); }}
                                            >
                                                <span className={styles.menuIcon}>&#9998;</span> Edit User
                                            </button>
                                            {canResetPassword && (
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => { setResetTarget({ userId: user.UserID, username: user.Username }); setOpenMenuId(null); }}
                                                >
                                                    <span className={styles.menuIcon}>&#128274;</span> Reset Password
                                                </button>
                                            )}
                                            <button
                                                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                                onClick={() => { setDeleteConfirm({ userId: user.UserID, username: user.Username }); setOpenMenuId(null); }}
                                            >
                                                <span className={styles.menuIcon}>&#128683;</span> Deactivate
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                    {showDeactivated && deactivatedUsers.map(user => (
                        <tr key={user.UserID} className={styles.deactivatedRow}>
                            <td>{user.Username} <span className={styles.deactivatedBadge}>Deactivated</span></td>
                            <td>{user.name || '-'}</td>
                            <td><span className={styles.roleBadge} data-role={user.Role}>{user.Role}</span></td>
                            <td>
                                {user.RegionalCodes && user.RegionalCodes.length > 0 ? (
                                    user.RegionalCodes.map(rc => rc.regionName).join(', ')
                                ) : (
                                    <span className={styles.noRegions}>No regions assigned</span>
                                )}
                            </td>
                            <td>
                                <button
                                    className={styles.restoreBtn}
                                    onClick={() => handleRestoreUser(user.UserID, user.Username)}
                                >
                                    Restore
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Add User Modal */}
            {showAddModal && (
                <div className={styles.modalBackdrop} onClick={() => { setShowAddModal(false); resetForm(); }}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>{isEditing ? 'Edit User' : 'Register New User'}</h3>
                            <button className={styles.closeBtn} onClick={() => { setShowAddModal(false); resetForm(); }}>&times;</button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.formGroup}>
                                <label>Username(Email) <span className={styles.required}>*</span></label>
                                <input
                                    type="text"
                                    value={formUsername}
                                    onChange={e => setFormUsername(e.target.value)}
                                    placeholder="Enter username"
                                    className={styles.input}
                                    autoComplete="off"
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Name <span className={styles.required}>*</span></label>
                                <input
                                    type="text"
                                    value={formName}
                                    onChange={e => setFormName(e.target.value)}
                                    placeholder="Enter full name"
                                    className={styles.input}
                                />
                            </div>
                            {!isEditing && (
                                <div className={styles.formGroup}>
                                    <label>Password <span className={styles.required}>*</span></label>
                                    <input
                                        type="password"
                                        value={formPassword}
                                        onChange={e => setFormPassword(e.target.value)}
                                        placeholder="Enter password"
                                        className={styles.input}
                                        autoComplete="new-password"
                                    />
                                </div>
                            )}
                            <div className={styles.formGroup}>
                                <label>Role <span className={styles.required}>*</span></label>
                                <select
                                    value={formRole}
                                    onChange={e => { setFormRole(e.target.value); setFormCities([]); setExpandedRegions({}); }}
                                    className={styles.input}
                                >
                                    <option value="">-- Select Role --</option>
                                    {AVAILABLE_ROLES.map(r => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                            {needsCities && (
                                <div className={styles.formGroup}>
                                    <label>Assign Cities / Regions <span className={styles.required}>*</span></label>
                                    <div className={styles.regionAccordionList}>
                                        {regions.length === 0 && <span className={styles.noRegions}>No regions available</span>}
                                        {regions.map(region => {
                                            const cities = regionCitiesMap[region.regionName] || [];
                                            const isOpen = expandedRegions[region.regionName];
                                            const selectedCount = cities.filter(c => formCities.includes(c)).length;
                                            const allSelected = cities.length > 0 && selectedCount === cities.length;
                                            return (
                                                <div key={region.regionCode} className={styles.regionAccordion}>
                                                    <div className={styles.regionHeader} onClick={() => toggleRegion(region.regionName)}>
                                                        <div className={styles.regionHeaderLeft}>
                                                            <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>&#9654;</span>
                                                            <span className={styles.regionLabel}>{region.regionName}</span>
                                                            <span className={styles.regionCode}>{region.regionCode}</span>
                                                        </div>
                                                        {selectedCount > 0 && (
                                                            <span className={styles.selectedBadge}>{selectedCount} selected</span>
                                                        )}
                                                    </div>
                                                    {isOpen && (
                                                        <div className={styles.regionCities}>
                                                            {cities.length > 0 && (
                                                                <label className={`${styles.checkboxLabel} ${styles.selectAllLabel}`}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={allSelected}
                                                                        onChange={() => handleRegionSelectAll(region.regionName)}
                                                                    />
                                                                    Select All ({cities.length})
                                                                </label>
                                                            )}
                                                            {cities.length === 0 && <span className={styles.noRegions}>No cities in this region</span>}
                                                            {cities.map(city => (
                                                                <label key={city} className={styles.checkboxLabel}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={formCities.includes(city)}
                                                                        onChange={() => handleCityToggle(city)}
                                                                    />
                                                                    {city}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {formCities.length > 0 && (
                                        <div className={styles.selectedCities}>
                                            {formCities.map(c => (
                                                <span key={c} className={styles.cityTag}>
                                                    {c}
                                                    <button onClick={() => handleCityToggle(c)}>&times;</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelBtn} onClick={() => { setShowAddModal(false); resetForm(); }}>Cancel</button>
                            <button className={styles.submitBtn} onClick={handleSaveUser} disabled={saving}>
                                {saving ? (isEditing ? 'Updating...' : 'Registering...') : (isEditing ? 'Update User' : 'Register User')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className={styles.modalBackdrop} onClick={() => setDeleteConfirm(null)}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ width: 400 }}>
                        <div className={styles.modalHeader}>
                            <h3>Confirm Deactivation</h3>
                            <button className={styles.closeBtn} onClick={() => setDeleteConfirm(null)}>&times;</button>
                        </div>
                        <div className={styles.modalBody}>
                            <p className={styles.confirmText}>
                                Are you sure you want to deactivate user <strong>"{deleteConfirm.username}"</strong>?
                                The user will no longer be able to log in.
                            </p>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelBtn} onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button className={styles.deactivateConfirmBtn} onClick={handleDeleteUser}>Deactivate</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Reset Password Modal */}
            {resetTarget && (
                <div className={styles.modalBackdrop} onClick={() => { setResetTarget(null); setNewPassword(''); setShowNewPassword(false); }}>
                    <div className={styles.resetModal} onClick={e => e.stopPropagation()}>
                        <div className={styles.resetModalIcon}>&#128274;</div>
                        <h3 className={styles.resetModalTitle}>Reset Password</h3>
                        <p className={styles.resetModalSub}>
                            Enter a new password for <strong>{resetTarget.username}</strong>
                        </p>
                        <div className={styles.formGroup}>
                            <label>New Password <span className={styles.required}>*</span></label>
                            <div className={styles.passwordInputWrap}>
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    className={styles.input}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className={styles.eyeBtn}
                                    onClick={() => setShowNewPassword(prev => !prev)}
                                    tabIndex={-1}
                                    title={showNewPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showNewPassword ? '🙈' : '👁️'}
                                </button>
                            </div>
                        </div>
                        <div className={styles.resetModalActions}>
                            <button className={styles.cancelBtn} onClick={() => { setResetTarget(null); setNewPassword(''); setShowNewPassword(false); }}>Cancel</button>
                            <button className={styles.submitBtn} onClick={handleResetPassword} disabled={resetting}>
                                {resetting ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserRegions;
